import React, {
  createContext,
  FC,
  useState,
  ReactNode,
  useContext,
  useEffect,
  cloneElement,
  isValidElement,
  ReactElement,
} from 'react';
import { BaseComponentProps } from '../../types';
import { AppDialog } from '../../components/app-dialog';
export interface ContextValues {
  showDialog: (dialog: ReactNode) => void;
  hideDialog: () => void;
}
export const AppDialogContext = createContext<ContextValues>({
  showDialog: () => undefined,
  hideDialog: () => undefined,
});
export type DialogProps = {
  showCloseDialog: (close: boolean) => void;
};
export const AppDialogProvider: FC<BaseComponentProps> = ({ children }) => {
  /**
   * State
   */
  const [dialog, setDialog] = useState<ReactNode | null>(null);
  const [showClose, setShowClose] = useState<boolean>(true);
  /**
   * Methods
   */
  const showDialog = (dialog: ReactNode) => {
    setDialog(dialog);
  };
  const hideDialog = () => {
    setDialog(null);
    setShowClose(true);
  };
  useEffect(() => {
    const handlePopState = () => {
      if (dialog) {
        hideDialog();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dialog]);

  /**
   * DOM
   */
  const value = { showDialog, hideDialog } as ContextValues;
  return (
    <AppDialogContext.Provider value={value}>
      <AppDialog isOpen={!!dialog} onClose={hideDialog} showClose={showClose}>
        {!!dialog && isValidElement(dialog)
          ? cloneElement(dialog as ReactElement<DialogProps>, {
              showCloseDialog: setShowClose,
            })
          : dialog}
      </AppDialog>
      {children}
    </AppDialogContext.Provider>
  );
};
export const useAppDialog = () => useContext(AppDialogContext);
